import { Box, Fade, List, ListItem, Popper, styled } from '@mui/material';
import classnames from 'classnames';
import { FunctionComponent, SVGProps } from 'react';

const PREFIX = 'TSMED-hubsReport-CellPopper';

const cellPopperClasses = {
  header: `${PREFIX}-header`,
  day: `${PREFIX}-day`,
  icon: `${PREFIX}-icon`,
  primary: `${PREFIX}-primary`,
  secondary: `${PREFIX}-secondary`,
  countBar: `${PREFIX}-countBar`,
  counts: `${PREFIX}-counts`,
  countsNumber: `${PREFIX}-countsNumber`,
  countsText: `${PREFIX}-countsText`,
  available: `${PREFIX}-available`,
  availableTrial: `${PREFIX}-availableTrial`,
  unavailable: `${PREFIX}-unavailable`,
  assigned: `${PREFIX}-assigned`,
};

const Root = styled(Box, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'column',
  background: '#2A3641',
  padding: spacing(1.5),
  borderRadius: 8,
  gap: spacing(0.5),
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.5)',
  alignItems: 'flex-start',
  [`.${cellPopperClasses.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    gap: spacing(3),
    [`.${cellPopperClasses.day}`]: {
      background: '#364B5F',
      padding: '2px 8px',
      borderRadius: 4,
      alignSelf: 'end',
      textTransform: 'capitalize',
      fontSize: '14px',
    },
    [`.${cellPopperClasses.icon}`]: {
      svg: {
        height: spacing(3),
        width: spacing(3),
        display: 'block',
        color: '#FFFFFF',
      },
    },
  },
  [`.${cellPopperClasses.primary}`]: {
    fontSize: '16px',
    fontWeight: 500,
  },
  [`.${cellPopperClasses.secondary}`]: {
    fontSize: '14px',
    color: '#B6B6B6',
  },
  [`.${cellPopperClasses.countBar}`]: {
    display: 'flex',
    gap: 2,
    paddingBlock: spacing(1),
    alignSelf: 'center',
    li: {
      width: 10,
      height: 10,
      borderRadius: 2,
      padding: 0,
    },
  },
  [`.${cellPopperClasses.counts}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '> div': {
      width: '100%',
      justifyContent: 'space-between',
      background: 'none',
      // padding: '0 8px',
      gap: 4,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  [`.${cellPopperClasses.countsNumber}`]: {
    fontWeight: 600,
    fontSize: '16px',
  },
  [`.${cellPopperClasses.countsText}`]: {
    fontWeight: 500,
    fontSize: '14px',
  },
  [`.${cellPopperClasses.available}`]: {
    color: '#83BC62',
    background: '#83BC62',
  },
  [`.${cellPopperClasses.availableTrial}`]: {
    color: '#FCE96A',
    background: '#FCE96A',
  },
  [`.${cellPopperClasses.unavailable}`]: {
    color: '#7F8083',
    background: '#7F8083',
  },
  [`.${cellPopperClasses.assigned}`]: {
    color: '#FF4848',
    background: '#FF4848',
  },
}));

export type CellPopperProps = {
  day: 'today' | 'tomorrow';
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  primaryText: string;
  secondaryText: string;
  availableCount: number;
  availableTrialCount: number;
  availableNonTrialCount: number;
  assignedCount: number;
  returningCount: number;
  anchorEl: HTMLElement | null;
};

export const CellPopper = ({
  day,
  icon: Icon,
  primaryText,
  secondaryText,
  availableCount,
  availableTrialCount,
  availableNonTrialCount,
  assignedCount,
  returningCount,
  anchorEl,
}: CellPopperProps) => {
  const open = Boolean(anchorEl);

  return (
    <Popper
      sx={{
        zIndex: 2,
      }}
      open={open}
      anchorEl={anchorEl}
    >
      <Fade in={open}>
        <Root>
          <Box className={cellPopperClasses.header}>
            <Box className={cellPopperClasses.icon}>
              <Icon />
            </Box>
            <Box className={cellPopperClasses.day}>{day}</Box>
          </Box>
          <Box className={cellPopperClasses.primary}>{primaryText}</Box>
          <Box className={cellPopperClasses.secondary}>{secondaryText}</Box>
          <List className={cellPopperClasses.countBar}>
            {Array(availableNonTrialCount)
              .fill(null)
              .map((_, index) => (
                <ListItem key={index} className={cellPopperClasses.available} />
              ))}
            {Array(availableTrialCount)
              .fill(null)
              .map((_, index) => (
                <ListItem
                  key={index}
                  className={cellPopperClasses.availableTrial}
                />
              ))}
            {Array(assignedCount)
              .fill(null)
              .map((_, index) => (
                <ListItem key={index} className={cellPopperClasses.assigned} />
              ))}
            {Array(returningCount)
              .fill(null)
              .map((_, index) => (
                <ListItem
                  key={index}
                  className={cellPopperClasses.unavailable}
                />
              ))}
          </List>
          <Box className={cellPopperClasses.counts}>
            <Box
              className={classnames({
                [cellPopperClasses.available]: Boolean(availableNonTrialCount),
                [cellPopperClasses.availableTrial]:
                  Boolean(availableTrialCount),
                [cellPopperClasses.unavailable]: !availableCount,
              })}
            >
              <Box className={cellPopperClasses.countsText}>Available</Box>
              <Box className={cellPopperClasses.countsNumber}>
                {availableCount}
              </Box>
            </Box>
            <Box
              className={classnames({
                [cellPopperClasses.assigned]: Boolean(assignedCount),
                [cellPopperClasses.unavailable]: !assignedCount,
              })}
            >
              <Box className={cellPopperClasses.countsText}>Assigned</Box>
              <Box className={cellPopperClasses.countsNumber}>
                {assignedCount}
              </Box>
            </Box>
          </Box>
        </Root>
      </Fade>
    </Popper>
  );
};
